import { Product } from 'shared/types/product/Product';
import { mapProduct } from './map-product';

/**
 * "Product IDs" variant of the V2 product slider (`slider_type` = 'Product IDs'): the CMS author lists
 * variant item-codes by hand, and the block shows exactly those variants in exactly that order. Used by
 * the server preload (to build the filter) and by the slider view.
 *
 * Replaces the three near-identical SKU parsers that used to live in product-slider-query.ts, the V2
 * slider and the V1 `product-tile-cms` slider, and the two copies of the map-and-sort below.
 */

/** CMS `product_ids`: comma-separated variant item-codes, e.g. `"96331B4E, 98611C"`. */
export const parseSkuCodes = (raw?: string): string[] =>
  raw
    ?.split(',')
    .map((code) => code.trim())
    .filter(Boolean) ?? [];

/** Algolia `filters` matching any listed variant (Algolia-facing `searchableAttributes` spelling). */
export const skuCodesFilter = (skuCodes: string[]): string =>
  skuCodes.map((sku) => `variants.searchableAttributes.item-code:'${sku}'`).join(' OR ');

/**
 * The block's products in the author's order. Each hit is mapped with the listed variant selected, so the
 * tile shows that variant (colour, image, code, link). Hits matching none of the codes are dropped, and a
 * product listed under several codes appears once, at its first listed position.
 *
 * Matched case-insensitively and against `sku` as well as `item-code`: the Algolia `filters` above are an
 * exact match, so a CMS entry whose casing differs from the index would return the product but fail to
 * pin its variant — and mapProduct then falls back to masterVariantID, which is force-set to the chrome
 * variant, so the tile would silently show the wrong finish rather than fail visibly.
 *
 * Deliberately NOT the index's new-first order: the author's list is an editorial choice.
 */
export const productsInSkuOrder = (hits: any[], skuCodes: string[], locale: string): Product[] => {
  const norm = (value: unknown) => String(value ?? '').trim().toLowerCase();
  const normalisedCodes = skuCodes.map(norm);

  return hits
    .map((hit) => {
      const order = normalisedCodes.findIndex((code) =>
        (hit?.variants ?? []).some(
          (variant: any) => norm(variant?.attributes?.['item-code']) === code || norm(variant?.sku) === code,
        ),
      );
      return order === -1 ? null : { order, product: mapProduct(hit, locale, skuCodes[order]) };
    })
    .filter((entry): entry is { order: number; product: Product } => entry !== null)
    .sort((a, b) => a.order - b.order)
    .map(({ product }) => product);
};
